/* eslint-disable no-alert */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Row, Col, Card, CardBody, CardTitle, Button,
  Modal,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import { post, get } from '../../helpers/api_helper';
import { Can } from '../../utilities/Can';
import BaseGrid from '../../components/Common/BaseGrid';

import InventoryFormModal from '../../components/Inventory/InventoryFormModal';

function Items(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
    timeOut: 8000,
  };

  const { t } = props;

  const [signal, setSignal] = useState(0);
  const [itemModal, setItemModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [usr, setUsr] = useState(null);

  const deleteItem = async (row) => {
    if (window.confirm(props.t('Are you sure you want to delete this item?'))) {
      await post('/api/inventory/delete', { id: row.id });
      setSignal(signal + 1);
    }
  };

  const editItem = (row) => {
    setItemModal(row);
  };

  const exportInventory = async () => {
    try {
      const responseData = await get('/api/inventory/export', {
        responseType: 'blob', // Tell Axios to expect a Blob response
      });

      // Create a Blob object from the response data
      const blob = new Blob([responseData], { type: 'text/csv' });

      const currentDate = new Date();

      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
      const day = String(currentDate.getDate()).padStart(2, '0');
      const hours = String(currentDate.getHours()).padStart(2, '0');
      const minutes = String(currentDate.getMinutes()).padStart(2, '0');

      const filename = `inventory-export-${year}-${month}-${day}-${hours}:${minutes}.csv`;

      // Create a download link
      const url = window.URL.createObjectURL(blob);

      // Create an anchor element and initiate download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastr.error('Error exporting inventory');
    }
  };

  const importInventory = async (event) => {
    try {
      event.preventDefault();
      setImporting(true);

      const { file } = event.target; // Extract the file from the event
      const formData = new FormData(); // Create FormData object
      formData.append('csv', file.files[0]); // Append file to FormData

      await post('/api/inventory/import', formData);

      setImportModal(false);
      toastr.success('Inventory imported successfully');
      setSignal(signal + 1);
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        toastr.error(`Error importing inventory: ${error.response.data}`);
      } else if (error.request) {
        // The request was made but no response was received
        toastr.error('Error importing inventory: No response received');
      }
    } finally {
      setImporting(false);
    }

    return false;
  };

  useEffect(() => {
    const fetchUser = async () => {
      const obj = await JSON.parse(localStorage.getItem('authUser'));
      setUsr(obj);
    };

    fetchUser();
  }, []);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xxl="12">
            <Card>
              <CardBody>
                <CardTitle className="h4">{t('Inventory')}</CardTitle>

                <Can i="see" this="inventory">
                  <div className="float-end">
                    <Button color="primary" className="me-2" size="sm" onClick={() => { setImportModal(true); }}>
                      <span className="mdi mdi-import" />
                      {' '}
                      {t('Import')}
                    </Button>
                    <Button color="primary" size="sm" onClick={exportInventory}>
                      <span className="mdi mdi-export" />
                      {' '}
                      {t('Export')}
                    </Button>
                  </div>
                  <div style={{ clear: 'right' }} />

                  {usr !== null && (
                    <BaseGrid
                      url="/api/inventory"
                      shouldUpdate={signal}
                      hasSearch
                      create={() => setItemModal({})}
                      config={
                      {
                        columns: {
                          thumb: {
                            label: '',
                            image: {
                              src: (item) => (`/api/inventory/thumbnail/${item.id}`),
                              class: 'rounded',
                              style: { maxHeight: '5rem', maxWidth: '5rem' },
                            },
                            translate: false,
                          },
                          stock_id: {
                            label: props.t('Stock ID'),
                            sort: true,
                            link: {
                              to: '/inventory/%id%',
                            },
                          },
                          sj_sku: {
                            label: props.t('SJ Ref'),
                            sort: true,
                            link: {
                              to: '/inventory/%id%',
                            },
                          },
                          manufacturer: {
                            label: props.t('Manufacturer'),
                            sort: true,
                          },
                          part_number: {
                            label: props.t('Part Number'),
                            sort: true,
                            link: {
                              to: '/inventory/%id%',
                            },
                          },
                          qty: {
                            label: props.t('Shelf Qty'),
                            sort: true,
                          },
                          ship_desc: {
                            label: props.t('Desc'),
                            sort: false,
                            link: {
                              to: '/inventory/%id%',
                            },
                          },
                          actions: {
                            label: 'Actions',
                            actions: [
                              {
                                key: 'edit_item',
                                icon: 'mdi mdi-pencil',
                                class: 'btn btn-sm btn-soft-warning text-dark',
                                alt: 'Edit Item',
                                click: editItem,
                              },
                              {
                                key: 'remove_item',
                                icon: 'mdi mdi-close-thick',
                                class: 'btn btn-sm btn-soft-danger text-dark',
                                alt: 'Delete Item',
                                click: deleteItem,
                                condition: () => (usr.roles.includes('Admin')),
                              },
                            ],
                          },
                        },
                        pagination: {
                          sort: 'id',
                          direction: 'desc',
                          perPage: 30,
                        },
                      }
                      }
                    />
                  )}

                  <InventoryFormModal
                    shown={itemModal !== false}
                    item={itemModal !== false ? itemModal : {}}
                    onSuccess={() => {
                      setSignal(signal + 1);
                      setItemModal(false);
                    }}
                  />

                  <Modal isOpen={importModal} toggle={() => setImportModal(!importModal)}>
                    <div className="modal-header">
                      <h5 className="modal-title mt-0">{props.t('Import Inventory Data')}</h5>
                    </div>
                    <div className="modal-body">
                      <p><strong>{t('The first row should contain the column names.')}</strong></p>
                      <p>{t('If a row has an empty id column, a new inventory will be created. If a row has an id column with a value, the inventory with that id will be updated.')}</p>
                      <p>{t('Download an export as a reference to column names and value formats')}</p>

                      <p className="text-danger font-13">{t('Note: Qtys are volatile! When qty is included, it can cause issues due to changes that take place while document is offline.')}</p>
                      <form onSubmit={importInventory} encType="multipart/form-data" method="post" action="/api/inventory/import" id="import-form">
                        <input type="file" name="file" id="file" accept=".csv" />
                      </form>
                    </div>
                    <div className="modal-footer">
                      <Button type="button" color="dark" onClick={() => setImportModal(!importModal)}>{props.t('Close')}</Button>
                      <Button type="submit" form="import-form" color="success" disabled={importing}>{importing ? props.t('Uploading...') : props.t('Upload')}</Button>
                    </div>
                  </Modal>
                </Can>

                <Can not i="see" this="inventory"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

Items.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(Items));
